import React, { useRef, useEffect, useState } from 'react';
import Currency from './Currency';

/**
 * Shared filter UI for the product listing pages (Product listing, Sale,
 * Special-Sale, Tester…). Fully controlled: it owns the immutable filter-state
 * updates on the page's `comman`/`setComman` and calls `onChange()` after every
 * change so the page can reset pagination + refetch.
 *
 * Desktop (>=768px): a sticky horizontal bar of dropdown pills + chips + sort,
 *   pinned just below the fixed site header (height measured at runtime).
 * Mobile  (<768px):  a floating "Filter" button that opens a slide-in drawer
 *   containing the same facets as vertical sections (the old mobile style).
 */
const facetName = (list, id) => {
    const found = (list || []).find(o => String(o.id) === String(id));
    return found ? found.name : id;
};

const TopFilterBar = ({ filters, comman, setComman, strings, priceBounds, priceInput, setPriceInput, onChange, hideBrand = false, hideCategory = false }) => {
    const barRef = useRef(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    // Height of the fixed site header — used to pin the sticky bar right below it.
    const [stickyTop, setStickyTop] = useState(76);

    // Native <details> don't behave like a dropdown menu: they stay open on
    // outside-click and allow several open at once. Enforce "only one open" +
    // "click anywhere outside the open panel closes it". (Desktop bar only.)
    useEffect(() => {
        const onPointer = (e) => {
            const bar = barRef.current;
            if (!bar) return;
            bar.querySelectorAll('details[open]').forEach((d) => {
                if (!d.contains(e.target)) d.open = false;
            });
        };
        const onToggle = (e) => {
            const bar = barRef.current;
            const d = e.target;
            if (!bar || !d || d.tagName !== 'DETAILS' || !d.open || !bar.contains(d)) return;
            bar.querySelectorAll('details[open]').forEach((other) => {
                if (other !== d) other.open = false;
            });
        };
        document.addEventListener('pointerdown', onPointer, true);
        document.addEventListener('toggle', onToggle, true);
        return () => {
            document.removeEventListener('pointerdown', onPointer, true);
            document.removeEventListener('toggle', onToggle, true);
        };
    }, []);

    // Measure the fixed header so the sticky bar sits exactly below it (never
    // hidden behind it) regardless of header height / breakpoint.
    useEffect(() => {
        const measure = () => {
            const hdr = document.querySelector('.fixedHeader');
            const h = hdr ? Math.round(hdr.getBoundingClientRect().height) : 0;
            if (h > 0) setStickyTop(h);
        };
        measure();
        const t = setTimeout(measure, 400); // re-measure after logo/fonts settle
        window.addEventListener('resize', measure);
        return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
    }, []);

    // Lock body scroll while the mobile drawer is open.
    useEffect(() => {
        document.body.classList.toggle('tfb-drawer-lock', drawerOpen);
        return () => document.body.classList.remove('tfb-drawer-lock');
    }, [drawerOpen]);

    if (!filters) return null;

    const fire = () => { if (typeof onChange === 'function') onChange(); };

    const toggle = (type, value) => {
        setComman(prev => {
            const list = Array.isArray(prev[type]) ? prev[type] : [];
            const next = list.map(String).includes(String(value))
                ? list.filter(v => String(v) !== String(value))
                : [...list, value];
            return { ...prev, [type]: next, page: 1 };
        });
        fire();
    };
    const setRating = (v) => { setComman(prev => ({ ...prev, rating: prev.rating === v ? 0 : v, page: 1 })); fire(); };
    const setSort = (v) => { setComman(prev => ({ ...prev, short: v, page: 1 })); fire(); };
    const removeArrayFilter = (type, id) => {
        setComman(prev => ({ ...prev, [type]: (prev[type] || []).filter(v => String(v) !== String(id)), page: 1 }));
        fire();
    };
    const clearScalarFilter = (type, empty) => { setComman(prev => ({ ...prev, [type]: empty, page: 1 })); fire(); };
    const applyPriceRange = () => {
        let from = priceInput.from === '' ? priceBounds.min : Math.max(priceBounds.min, parseFloat(priceInput.from));
        let to = priceInput.to === '' ? priceBounds.max : Math.min(priceBounds.max, parseFloat(priceInput.to));
        if (isNaN(from)) from = priceBounds.min;
        if (isNaN(to)) to = priceBounds.max;
        if (from > to) { const t = from; from = to; to = t; }
        setPriceInput({ from: String(from), to: String(to) });
        setComman(prev => ({ ...prev, min_price: from, max_price: to, page: 1 }));
        fire();
    };
    const clearPriceFilter = () => {
        setPriceInput({ from: String(priceBounds.min), to: String(priceBounds.max) });
        setComman(prev => ({ ...prev, min_price: null, max_price: null, page: 1 }));
        fire();
    };
    const clearAllFilters = () => {
        setComman(prev => ({
            ...prev,
            category: [], brand: [], rating: 0, notes: [], perfumeTypes: [],
            short: '', min_price: null, max_price: null, page: 1
        }));
        setPriceInput({ from: String(priceBounds.min), to: String(priceBounds.max) });
        fire();
    };

    const cat = comman.category || [];
    const brd = comman.brand || [];
    const nts = comman.notes || [];
    const pts = comman.perfumeTypes || [];

    const showCategory = !hideCategory && filters.category && filters.category.length > 0;
    const showBrand = !hideBrand && filters.brand && filters.brand.length > 0;
    const showNotes = filters.notes && filters.notes.length > 0;
    const showTypes = filters.perfumeType && filters.perfumeType.length > 0;

    const activeCount =
        (hideCategory ? 0 : cat.length) +
        (hideBrand ? 0 : brd.length) +
        nts.length + pts.length +
        (comman.rating > 0 ? 1 : 0) +
        (comman.min_price != null && comman.max_price != null ? 1 : 0) +
        (comman.short ? 1 : 0);
    const hasActive = activeCount > 0;

    // ---- shared option-list renderers (used by both the desktop bar & the mobile drawer) ----
    const listCategory = (kp) => filters.category.map((option) => (
        <label className="custom_checkbox" key={`${kp}-cat-${option.id}`}>
            <input type="checkbox" checked={cat.map(String).includes(String(option.id))} onChange={() => toggle('category', option.id)} />
            <span className="checkmark"></span>{option.name}<span className="filter_meta">{option.total_product}</span>
        </label>
    ));
    const listBrand = (kp) => filters.brand.map((option) => (
        <label className="custom_checkbox" key={`${kp}-brand-${option.id}`}>
            <input type="checkbox" checked={brd.map(String).includes(String(option.id))} onChange={() => toggle('brand', option.id)} />
            <span className="checkmark"></span>{option.name}<span className="filter_meta">{option.total_product}</span>
        </label>
    ));
    const listRating = (kp) => [{ v: 5, l: strings.start45 }, { v: 4, l: strings.start34 }, { v: 3, l: strings.start23 }, { v: 2, l: strings.start12 }, { v: 1, l: strings.start01 }].map((r) => (
        <label className="custom_radio_b" key={`${kp}-rate-${r.v}`}>
            <input type="radio" name={`${kp}_rating`} checked={comman.rating === r.v} onChange={() => setRating(r.v)} />
            <span className="checkmark"></span>{r.l}
        </label>
    ));
    const listNotes = (kp) => filters.notes.map((option) => (
        <label className="custom_checkbox custom_checkbox_accords" style={{ background: option.color }} key={`${kp}-note-${option.id}`}>
            <input type="checkbox" checked={nts.map(String).includes(String(option.id))} onChange={() => toggle('notes', option.id)} />
            <span className="checkmark checkmark_accords"></span>{option.name}
        </label>
    ));
    const listTypes = (kp) => filters.perfumeType.map((option) => (
        <label className="custom_checkbox custom_checkbox_accords" style={{ background: option.color }} key={`${kp}-type-${option.id}`}>
            <input type="checkbox" checked={pts.map(String).includes(String(option.id))} onChange={() => toggle('perfumeTypes', option.id)} />
            <span className="checkmark checkmark_accords"></span>{option.name}
        </label>
    ));
    const listSort = (kp) => (
        <>
            <label className="custom_radio_b">
                <input type="radio" name={`${kp}_short`} value="A_TO_Z" checked={comman.short === 'A_TO_Z'} onChange={() => setSort('A_TO_Z')} />
                <span className="checkmark"></span>A To Z
            </label>
            <label className="custom_radio_b">
                <input type="radio" name={`${kp}_short`} value="Z_TO_A" checked={comman.short === 'Z_TO_A'} onChange={() => setSort('Z_TO_A')} />
                <span className="checkmark"></span>Z To A
            </label>
        </>
    );
    const priceFilter = (
        <div className="tfb_price_filter">
            <div className="tfb_price_avail">
                {strings.lowest || 'Lowest'}<Currency data={priceBounds.min} />
                <span className="tfb_price_sep">·</span>
                {strings.highest || 'Highest'}<Currency data={priceBounds.max} />
            </div>
            <div className="tfb_price_inputs">
                <div className="tfb_price_field">
                    <span>{strings.from || 'From'}</span>
                    <input type="number" min={priceBounds.min} max={priceBounds.max} value={priceInput.from} placeholder={String(priceBounds.min)} onChange={(e) => setPriceInput((p) => ({ ...p, from: e.target.value }))} />
                </div>
                <span className="tfb_price_dash">–</span>
                <div className="tfb_price_field">
                    <span>{strings.to || 'To'}</span>
                    <input type="number" min={priceBounds.min} max={priceBounds.max} value={priceInput.to} placeholder={String(priceBounds.max)} onChange={(e) => setPriceInput((p) => ({ ...p, to: e.target.value }))} />
                </div>
            </div>
            <button type="button" className="tfb_price_apply" onClick={applyPriceRange}>{strings.apply || 'Apply'}</button>
        </div>
    );

    return (
        <>
            {/* ===== Desktop: sticky horizontal bar ===== */}
            <div className="top_filter_bar" ref={barRef} style={{ top: stickyTop }}>
                <div className="tfb_pills">
                    {showCategory && (
                        <details className={`tfb_pill ${cat.length > 0 ? 'tfb_pill_active' : ''}`}>
                            <summary>{strings.category}{cat.length > 0 && <span className="tfb_pill_count">{cat.length}</span>}<span className="tfb_caret">▾</span></summary>
                            <div className="tfb_panel"><div className="filter_cont_s">{listCategory('bar')}</div></div>
                        </details>
                    )}
                    {showBrand && (
                        <details className={`tfb_pill ${brd.length > 0 ? 'tfb_pill_active' : ''}`}>
                            <summary>{strings.brands}{brd.length > 0 && <span className="tfb_pill_count">{brd.length}</span>}<span className="tfb_caret">▾</span></summary>
                            <div className="tfb_panel"><div className="filter_cont_s">{listBrand('bar')}</div></div>
                        </details>
                    )}
                    <details className={`tfb_pill ${comman.rating > 0 ? 'tfb_pill_active' : ''}`}>
                        <summary>{strings.rating}<span className="tfb_caret">▾</span></summary>
                        <div className="tfb_panel"><div className="filter_cont_s">{listRating('bar')}</div></div>
                    </details>
                    <details className={`tfb_pill ${(comman.min_price != null && comman.max_price != null) ? 'tfb_pill_active' : ''}`}>
                        <summary>{strings.price}<span className="tfb_caret">▾</span></summary>
                        <div className="tfb_panel"><div className="filter_cont_s">{priceFilter}</div></div>
                    </details>
                    {showNotes && (
                        <details className={`tfb_pill ${nts.length > 0 ? 'tfb_pill_active' : ''}`}>
                            <summary>{strings.perfume_accords}{nts.length > 0 && <span className="tfb_pill_count">{nts.length}</span>}<span className="tfb_caret">▾</span></summary>
                            <div className="tfb_panel"><div className="filter_cont_s">{listNotes('bar')}</div></div>
                        </details>
                    )}
                    {showTypes && (
                        <details className={`tfb_pill ${pts.length > 0 ? 'tfb_pill_active' : ''}`}>
                            <summary>{strings.perfumeType}{pts.length > 0 && <span className="tfb_pill_count">{pts.length}</span>}<span className="tfb_caret">▾</span></summary>
                            <div className="tfb_panel"><div className="filter_cont_s">{listTypes('bar')}</div></div>
                        </details>
                    )}
                </div>

                <div className="tfb_chips">
                    {!hideCategory && cat.map((id) => (
                        <span className="tfb_chip" key={`chip-cat-${id}`}>{facetName(filters.category, id)}<span className="tfb_chip_x" onClick={() => removeArrayFilter('category', id)}>×</span></span>
                    ))}
                    {!hideBrand && brd.map((id) => (
                        <span className="tfb_chip" key={`chip-brand-${id}`}>{facetName(filters.brand, id)}<span className="tfb_chip_x" onClick={() => removeArrayFilter('brand', id)}>×</span></span>
                    ))}
                    {nts.map((id) => (
                        <span className="tfb_chip" key={`chip-note-${id}`}>{facetName(filters.notes, id)}<span className="tfb_chip_x" onClick={() => removeArrayFilter('notes', id)}>×</span></span>
                    ))}
                    {pts.map((id) => (
                        <span className="tfb_chip" key={`chip-type-${id}`}>{facetName(filters.perfumeType, id)}<span className="tfb_chip_x" onClick={() => removeArrayFilter('perfumeTypes', id)}>×</span></span>
                    ))}
                    {comman.rating > 0 && (
                        <span className="tfb_chip">{comman.rating}★ &amp; up<span className="tfb_chip_x" onClick={() => clearScalarFilter('rating', 0)}>×</span></span>
                    )}
                    {comman.min_price != null && comman.max_price != null && (
                        <span className="tfb_chip"><Currency data={comman.min_price} /> –<Currency data={comman.max_price} /><span className="tfb_chip_x" onClick={clearPriceFilter}>×</span></span>
                    )}
                    {comman.short && (
                        <span className="tfb_chip">{comman.short === 'A_TO_Z' ? 'A → Z' : 'Z → A'}<span className="tfb_chip_x" onClick={() => clearScalarFilter('short', '')}>×</span></span>
                    )}
                    {hasActive && (
                        <button type="button" className="tfb_clear" onClick={clearAllFilters}>{strings.clearAll || 'Clear All'}</button>
                    )}
                </div>

                <div className="tfb_sort">
                    <details className={`tfb_pill ${comman.short ? 'tfb_pill_active' : ''}`}>
                        <summary>{strings.short_by}<span className="tfb_caret">▾</span></summary>
                        <div className="tfb_panel"><div className="filter_cont_s">{listSort('bar')}</div></div>
                    </details>
                </div>
            </div>

            {/* ===== Mobile: floating button + slide-in drawer ===== */}
            <button type="button" className="tfb-fab" onClick={() => setDrawerOpen(true)} aria-label="Filters">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="4" y1="6" x2="20" y2="6" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="10" y1="18" x2="14" y2="18" />
                </svg>
                <span>{strings.filter || 'Filter'}</span>
                {activeCount > 0 && <span className="tfb-fab-count">{activeCount}</span>}
            </button>

            <div className={`tfb-drawer-backdrop ${drawerOpen ? 'show' : ''}`} onClick={() => setDrawerOpen(false)}></div>
            <aside className={`tfb-drawer ${drawerOpen ? 'show' : ''}`} aria-hidden={!drawerOpen}>
                <div className="tfb-drawer-head">
                    <h3>{strings.filter || 'Filter'}</h3>
                    <button type="button" className="tfb-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close">×</button>
                </div>
                <div className="tfb-drawer-body">
                    <div className="tfb-dsec">
                        <h4>{strings.short_by}</h4>
                        <div className="filter_cont_s">{listSort('drw')}</div>
                    </div>
                    {showCategory && (
                        <div className="tfb-dsec">
                            <h4>{strings.category}</h4>
                            <div className="filter_cont_s">{listCategory('drw')}</div>
                        </div>
                    )}
                    {showBrand && (
                        <div className="tfb-dsec">
                            <h4>{strings.brands}</h4>
                            <div className="filter_cont_s">{listBrand('drw')}</div>
                        </div>
                    )}
                    <div className="tfb-dsec">
                        <h4>{strings.rating}</h4>
                        <div className="filter_cont_s">{listRating('drw')}</div>
                    </div>
                    <div className="tfb-dsec">
                        <h4>{strings.price}</h4>
                        <div className="filter_cont_s">{priceFilter}</div>
                    </div>
                    {showNotes && (
                        <div className="tfb-dsec tfb-dsec--wrap">
                            <h4>{strings.perfume_accords}</h4>
                            <div className="filter_cont_s">{listNotes('drw')}</div>
                        </div>
                    )}
                    {showTypes && (
                        <div className="tfb-dsec tfb-dsec--wrap">
                            <h4>{strings.perfumeType}</h4>
                            <div className="filter_cont_s">{listTypes('drw')}</div>
                        </div>
                    )}
                </div>
                <div className="tfb-drawer-foot">
                    {hasActive && <button type="button" className="tfb-drawer-clear" onClick={clearAllFilters}>{strings.clearAll || 'Clear All'}</button>}
                    <button type="button" className="tfb-drawer-apply" onClick={() => setDrawerOpen(false)}>{strings.apply || 'Show results'}</button>
                </div>
            </aside>
        </>
    );
};

export default TopFilterBar;
